import { observeWhenVisible } from '../../core/utils/dom.js';
import { applyTranslations, getCurrentLanguage } from '../translations.js';
import { getAuthToken, getUserCredits, syncSpriteCredits } from './videoScheduleApi.js';
import { computeVideoUnitCost, countVideoLoraEntries, interpolateCreditTemplate, loadCreditPricing } from './creditCostApi.js';
import { waitForQueuedImage } from './waitForQueuedImage.js';
import { notifyVideoContainerStarting, waitForQueuedVideo } from './waitForQueuedVideo.js';
import { adoptWaitEstimate, remainingWaitSeconds } from './waitEstimate.js';
import { showConfirm, showError, showSuccess } from '../utils/UnifiedNotificationSystem.js';
import {
  fetchStandaloneImageObjectUrl,
  hdUpgradeCostLabel,
  requestStandaloneHdUpgrade,
  requestStandaloneImageDelete,
  revokeStandaloneImageObjectUrl,
  standaloneLoraList,
  standalonePromptText,
  standaloneNegativeText,
} from './standaloneHdUpgrade.js';
import { writeSimilarStandaloneImage } from './similarStandaloneImage.js';
import { writeSimilarStandaloneVideo } from './similarStandaloneVideo.js';
import { parseGenerationSeed } from './generationSeed.js';
import { clampStandaloneImageSize } from './videoPage/standaloneImageSize.js';
import {
  fetchStandaloneMediaPage,
  fetchStandaloneVideoObjectUrl,
  PAGE_LIMIT,
  requestStandaloneVideoDelete,
  requestStandaloneVideoHdUpgrade,
  revokeStandaloneVideoObjectUrl,
} from './standaloneMediaApi.js';
import { createGalleryListCache } from './galleryListCache.js';

const getT = (key, fallback) => {
  const lang = getCurrentLanguage() || 'en';
  const t = window.translations?.[lang]?.['my-gallery'] ?? window.translations?.en?.['my-gallery'];
  return t?.[key] ?? fallback;
};

const isUserLoggedIn = async () => {
  const token = await getAuthToken();
  return Boolean(token);
};

const filterSuffix = (filter) => (filter === 'videos' || filter === 'images' ? filter : 'both');

const isStandaloneMediaReady = (item) => {
  if (item?.media_kind === 'video') return item.status === 1 || item.status === 4;
  return item.status === 1 && !item.hires_pending;
};

const renderStatus = (gridEl, kind, filter = 'both') => {
  gridEl.replaceChildren();
  const status = document.createElement('div');
  status.className = 'my-gallery-status';

  const message = document.createElement('p');
  message.className = 'my-gallery-status__message';
  const suffix = filterSuffix(filter);

  if (kind === 'loading') {
    const key = `loading_${suffix}`;
    message.setAttribute('data-translate', key);
    message.textContent = getT(key, getT('loading', 'Loading…'));
    status.appendChild(message);
  } else if (kind === 'login') {
    const key = `login_prompt_${suffix}`;
    message.setAttribute('data-translate', key);
    message.textContent = getT(key, getT('login_prompt', 'Log in to see your images and videos.'));
    const loginButton = document.createElement('button');
    loginButton.type = 'button';
    loginButton.className = 'my-gallery-login-btn';
    loginButton.setAttribute('data-translate', 'login_button');
    loginButton.textContent = getT('login_button', 'Log in');
    loginButton.tabIndex = 0;
    loginButton.setAttribute('aria-label', getT('login_button', 'Log in'));
    loginButton.addEventListener('click', async () => {
      try {
        const { default: loginModalController } = await import('../components/LoginModalController.js');
        loginModalController?.show?.();
      } catch (error) {
        console.error('MyGallery: failed to open login', error);
      }
    });
    status.appendChild(message);
    status.appendChild(loginButton);
  } else if (kind === 'error') {
    const key = `error_${suffix}`;
    message.setAttribute('data-translate', key);
    message.textContent = getT(key, getT('error', 'Could not load media.'));
    status.appendChild(message);
  } else if (kind === 'empty_filtered') {
    const key = `empty_filtered_${suffix}`;
    message.setAttribute('data-translate', key);
    message.textContent = getT(key, getT('empty_filtered', 'No items match these filters.'));
    status.appendChild(message);
  } else {
    const key = `empty_${suffix}`;
    message.setAttribute('data-translate', key);
    message.textContent = getT(key, getT('empty', 'No images or videos yet.'));
    status.appendChild(message);
  }

  gridEl.appendChild(status);
  const currentLang = getCurrentLanguage();
  if (currentLang) applyTranslations('my-gallery', currentLang);
};

const appendDialogPending = (modal) => {
  const media = modal.querySelector('.my-gallery-dialog__media');
  if (!media || media.querySelector('#my-gallery-dialog-pending')) return;
  const overlay = document.createElement('div');
  overlay.id = 'my-gallery-dialog-pending';
  overlay.className = 'my-gallery-pending-overlay my-gallery-dialog__pending hidden';
  const label = document.createElement('p');
  label.id = 'my-gallery-dialog-eta';
  label.className = 'my-gallery-pending-overlay__label';
  overlay.appendChild(label);
  media.appendChild(overlay);
};

const ensureDialog = () => {
  let modal = document.getElementById('my-gallery-dialog');
  if (modal) {
    appendDialogPending(modal);
    return modal;
  }

  modal = document.createElement('div');
  modal.id = 'my-gallery-dialog';
  modal.className = 'my-gallery-dialog';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', 'my-gallery-dialog-title');
  modal.innerHTML = `
    <div class="my-gallery-dialog__panel" role="document">
      <div class="my-gallery-dialog__header">
        <h3 id="my-gallery-dialog-title" class="my-gallery-dialog__title">Image</h3>
        <button type="button" class="my-gallery-dialog__close" id="my-gallery-dialog-close" tabindex="0" data-translate="close" aria-label="Close">Close</button>
      </div>
      <div class="my-gallery-dialog__media">
        <img class="my-gallery-dialog__image" id="my-gallery-dialog-image" alt="">
        <video class="my-gallery-dialog__image hidden" id="my-gallery-dialog-video" autoplay loop muted playsinline></video>
        <button type="button" class="my-gallery-dialog__download" id="my-gallery-dialog-download" tabindex="0" data-translate-attr="aria-label:download" aria-label="Download" disabled>
          <i class="fas fa-download" aria-hidden="true"></i>
        </button>
        <div class="my-gallery-pending-overlay my-gallery-dialog__pending hidden" id="my-gallery-dialog-pending">
          <p class="my-gallery-pending-overlay__label" id="my-gallery-dialog-eta"></p>
        </div>
      </div>
      <p class="my-gallery-dialog__prompt-label" data-translate="prompt_label">Prompt</p>
      <p class="my-gallery-dialog__prompt" id="my-gallery-dialog-prompt"></p>
      <p class="my-gallery-dialog__loras-label" data-translate="loras_label">LoRAs</p>
      <ul class="my-gallery-dialog__loras" id="my-gallery-dialog-loras"></ul>
      <div class="my-gallery-dialog__facts" id="my-gallery-dialog-facts">
        <div class="my-gallery-dialog__fact" id="my-gallery-dialog-size-group">
          <p class="my-gallery-dialog__size-label" data-translate="dimensions_label">Dimensions</p>
          <p class="my-gallery-dialog__size" id="my-gallery-dialog-size"></p>
        </div>
        <div class="my-gallery-dialog__fact" id="my-gallery-dialog-seed-group">
          <p class="my-gallery-dialog__seed-label" data-translate="seed_label">Seed</p>
          <p class="my-gallery-dialog__seed" id="my-gallery-dialog-seed"></p>
        </div>
      </div>
      <button type="button" class="my-gallery-login-btn my-gallery-dialog__upgrade" id="my-gallery-dialog-upgrade" tabindex="0" data-translate="upgrade_hd">Upgrade to HD</button>
      <p class="my-gallery-dialog__credits" id="my-gallery-dialog-credits"></p>
      <p class="my-gallery-dialog__done hidden" id="my-gallery-dialog-done">
        <i class="fas fa-check" aria-hidden="true"></i>
        <span data-translate="upgraded_hd">Upgraded to HD</span>
      </p>
      <button type="button" class="my-gallery-login-btn my-gallery-dialog__upgrade" id="my-gallery-dialog-to-video" tabindex="0" data-translate="upgrade_to_video">Upgrade to video</button>
      <p class="my-gallery-dialog__hint hidden" id="my-gallery-dialog-video-hint" data-translate="upgrade_to_video_hint">Upgrade this image to HD before creating a video.</p>
      <div class="my-gallery-dialog__actions">
        <button type="button" class="my-gallery-login-btn" id="my-gallery-dialog-similar" tabindex="0" data-translate="create_similar" aria-label="Create similar image">Create similar image</button>
        <button type="button" class="my-gallery-login-btn my-gallery-dialog__delete" id="my-gallery-dialog-delete" tabindex="0" data-translate="delete_image" aria-label="Delete image">Delete image</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  appendDialogPending(modal);
  return modal;
};

const PROMPT_FILTER_MAX = 200;
const PROMPT_DEBOUNCE_MS = 300;

const syncTabState = (tabs, attr, value) => {
  tabs.forEach((tab) => {
    const isActive = tab.dataset[attr] === value;
    tab.classList.toggle('create-mode-tab--active', isActive);
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });
};

export const startMyGalleryPage = () => {
  const gridEl = document.getElementById('my-gallery-grid');
  const triggerEl = document.getElementById('infinite-scroll-trigger');
  const filterTabs = Array.from(document.querySelectorAll('[data-tablist="media-filter"] [role="tab"]'));
  const hdTabs = Array.from(document.querySelectorAll('[data-tablist="hd-filter"] [role="tab"]'));
  const promptInput = document.getElementById('my-gallery-prompt');
  const countEl = document.getElementById('my-gallery-count');
  if (!gridEl) return;

  const mediaFromQuery = () => {
    const raw = new URLSearchParams(window.location.search).get('media');
    return ['images', 'videos', 'both'].includes(raw) ? raw : 'both';
  };
  const hdFromQuery = () => {
    const raw = new URLSearchParams(window.location.search).get('hd');
    return ['lowres', 'hires', 'both'].includes(raw) ? raw : 'both';
  };
  const promptFromQuery = () => (
    String(new URLSearchParams(window.location.search).get('prompt') || '').trim().slice(0, PROMPT_FILTER_MAX)
  );
  let mediaFilter = mediaFromQuery();
  let hdFilter = hdFromQuery();
  let promptFilter = promptFromQuery();
  let matchedCount = 0;
  let promptTimer = null;
  if (promptInput) promptInput.value = promptFilter;

  const itemByKey = new Map();
  const listCache = createGalleryListCache();
  const waitControllers = new Map();
  const etaEstimates = new Map();
  let etaTickTimer = null;
  const modal = ensureDialog();
  const closeBtn = modal.querySelector('#my-gallery-dialog-close');
  const dialogImg = modal.querySelector('#my-gallery-dialog-image');
  const dialogVideo = modal.querySelector('#my-gallery-dialog-video');
  const dialogTitle = modal.querySelector('#my-gallery-dialog-title');
  const dialogPrompt = modal.querySelector('#my-gallery-dialog-prompt');
  const dialogLoras = modal.querySelector('#my-gallery-dialog-loras');
  const dialogFacts = modal.querySelector('#my-gallery-dialog-facts');
  const dialogSizeGroup = modal.querySelector('#my-gallery-dialog-size-group');
  const dialogSize = modal.querySelector('#my-gallery-dialog-size');
  const dialogSeedGroup = modal.querySelector('#my-gallery-dialog-seed-group');
  const dialogSeed = modal.querySelector('#my-gallery-dialog-seed');
  const dialogUpgrade = modal.querySelector('#my-gallery-dialog-upgrade');
  const dialogToVideo = modal.querySelector('#my-gallery-dialog-to-video');
  const dialogVideoHint = modal.querySelector('#my-gallery-dialog-video-hint');
  const dialogCredits = modal.querySelector('#my-gallery-dialog-credits');
  const dialogDone = modal.querySelector('#my-gallery-dialog-done');
  const dialogSimilar = modal.querySelector('#my-gallery-dialog-similar');
  const dialogDelete = modal.querySelector('#my-gallery-dialog-delete');
  const dialogDownload = modal.querySelector('#my-gallery-dialog-download');
  const dialogPending = modal.querySelector('#my-gallery-dialog-pending');
  const dialogEta = modal.querySelector('#my-gallery-dialog-eta');
  const dialogMedia = modal.querySelector('.my-gallery-dialog__media');
  let openKey = null;
  const upgradePostingKeys = new Set();

  const hasExtraFilters = () => Boolean(promptFilter) || hdFilter !== 'both';

  const emptyKind = () => (hasExtraFilters() ? 'empty_filtered' : 'empty');

  const paintCount = (visible) => {
    if (!countEl) return;
    if (!visible) {
      countEl.hidden = true;
      countEl.textContent = '';
      return;
    }
    countEl.hidden = false;
    countEl.textContent = getT('count', '{count} items').replace('{count}', String(matchedCount));
  };

  const syncGalleryQuery = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('media', mediaFilter);
    if (hdFilter === 'both') url.searchParams.delete('hd');
    else url.searchParams.set('hd', hdFilter);
    if (!promptFilter) url.searchParams.delete('prompt');
    else url.searchParams.set('prompt', promptFilter);
    window.history.replaceState({}, '', url);
  };

  const abortAllWaits = () => {
    waitControllers.forEach((controller) => controller.abort());
    waitControllers.clear();
    etaEstimates.clear();
    if (etaTickTimer) {
      clearInterval(etaTickTimer);
      etaTickTimer = null;
    }
  };

  const etaLabel = (outputKey) => {
    const estimate = etaEstimates.get(outputKey);
    if (estimate?.stalled) return getT('eta_stalled', 'Instance stalled, requeued');
    const remaining = remainingWaitSeconds(estimate);
    if (remaining != null && remaining > 0) {
      return getT('eta_remaining', 'About {seconds}s remaining').replace('{seconds}', String(remaining));
    }
    return getT('eta_waiting', 'Creating…');
  };

  const paintEta = (outputKey) => {
    const label = etaLabel(outputKey);
    const card = gridEl.querySelector(`[data-output-key="${CSS.escape(String(outputKey))}"]`);
    card?.querySelectorAll('.my-gallery-eta').forEach((el) => {
      el.textContent = label;
    });
    if (openKey === outputKey && dialogEta && !dialogPending?.classList.contains('hidden')) {
      dialogEta.textContent = label;
    }
  };

  const ensureEtaTicker = () => {
    if (etaTickTimer) return;
    etaTickTimer = setInterval(() => {
      waitControllers.forEach((_, key) => paintEta(key));
      if (waitControllers.size === 0 && etaTickTimer) {
        clearInterval(etaTickTimer);
        etaTickTimer = null;
      }
    }, 1000);
  };

  const cardForKey = (outputKey) => (
    gridEl.querySelector(`[data-output-key="${CSS.escape(String(outputKey))}"]`)
  );

  const appendEtaOverlay = (parent, outputKey) => {
    const overlay = document.createElement('div');
    overlay.className = 'my-gallery-pending-overlay';
    const label = document.createElement('p');
    label.className = 'my-gallery-pending-overlay__label my-gallery-eta';
    label.textContent = etaLabel(outputKey);
    overlay.appendChild(label);
    parent.appendChild(overlay);
  };

  const gridVideos = () => gridEl.querySelectorAll('video');

  const isDialogOpen = () => modal.classList.contains('show');

  const pauseGridVideos = () => {
    gridVideos().forEach((video) => {
      video.pause();
    });
  };

  const resumeGridVideos = () => {
    gridVideos().forEach((video) => {
      if (!video.src) return;
      void video.play().catch(() => {});
    });
  };

  const fillCardMedia = (card, item, { pending = false } = {}) => {
    card.replaceChildren();
    card.classList.toggle('is-pending', pending);
    card.classList.toggle('has-preview', pending);
    card.disabled = false;
    card.setAttribute('aria-label', item.media_kind === 'video'
      ? getT('open_video', 'Open video')
      : getT('open_image', 'Open image'));
    if (item.media_kind === 'video') {
      const video = document.createElement('video');
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'metadata';
      void (async () => {
        try {
          const token = await getAuthToken();
          const objectUrl = await fetchStandaloneVideoObjectUrl({ token, outputKey: item.output_key });
          if (objectUrl && video.isConnected) {
            video.src = objectUrl;
            if (!isDialogOpen()) void video.play().catch(() => {});
          }
        } catch {
          // Lowres file is not available yet.
        }
      })();
      card.appendChild(video);
      if (pending) appendEtaOverlay(card, item.output_key);
      return;
    }
    const img = document.createElement('img');
    img.alt = '';
    img.loading = 'lazy';
    void (async () => {
      try {
        const token = await getAuthToken();
        const objectUrl = await fetchStandaloneImageObjectUrl({ token, outputKey: item.output_key });
        if (objectUrl && img.isConnected) img.src = objectUrl;
      } catch {
        // Lowres file is not available yet.
      }
    })();
    card.appendChild(img);
    if (pending) appendEtaOverlay(card, item.output_key);
  };

  const paintTimerOnlyCard = (card, item) => {
    card.replaceChildren();
    card.classList.add('is-pending');
    card.classList.remove('has-preview');
    card.disabled = false;
    card.setAttribute('aria-label', item.media_kind === 'video'
      ? getT('open_video', 'Open video')
      : getT('open_image', 'Open image'));
    const eta = document.createElement('p');
    eta.className = 'my-gallery-card__eta my-gallery-eta';
    eta.textContent = etaLabel(item.output_key);
    card.appendChild(eta);
  };

  const markCardFailed = (outputKey) => {
    const text = getT('eta_failed', 'Generation failed');
    cardForKey(outputKey)?.querySelectorAll('.my-gallery-eta').forEach((el) => {
      el.textContent = text;
    });
    if (openKey === outputKey && dialogEta) dialogEta.textContent = text;
  };

  const refreshCardMedia = (updated) => {
    const card = cardForKey(updated.output_key);
    if (!card) return;
    fillCardMedia(card, updated);
  };

  const finishUpgradeFailure = (item) => {
    const key = item.output_key;
    waitControllers.delete(key);
    etaEstimates.delete(key);
    const restored = item.media_kind === 'video'
      ? { ...item, status: 4 }
      : { ...item, hires_pending: false };
    itemByKey.set(key, restored);
    listCache.upsertItem(restored);
    refreshCardMedia(restored);
    if (openKey === key) void fillDialog(restored);
  };

  const watchJob = (item, { requireHd = false } = {}) => {
    const key = item.output_key;
    waitControllers.get(key)?.abort();
    const controller = new AbortController();
    waitControllers.set(key, controller);
    ensureEtaTicker();
    let lastData = null;
    const handleEstimate = (data) => {
      lastData = data;
      etaEstimates.set(key, adoptWaitEstimate(etaEstimates.get(key), data));
      paintEta(key);
      notifyVideoContainerStarting(data, key, item.media_kind === 'video' ? 'video' : 'image');
      if (data?.status === 'failed') markCardFailed(key);
    };
    const handleFailure = (error) => {
      if (error?.name === 'AbortError') return true;
      if (!/failed/i.test(error?.message || '')) return false;
      if (requireHd) {
        console.error('MyGallery upgrade failed', error);
        finishUpgradeFailure(item);
        return true;
      }
      markCardFailed(key);
      waitControllers.delete(key);
      etaEstimates.delete(key);
      return true;
    };
    void (async () => {
      try {
        while (!controller.signal.aborted) {
          try {
            if (item.media_kind === 'video') {
              await waitForQueuedVideo({
                outputKey: key,
                signal: controller.signal,
                onEstimate: handleEstimate,
                requireHd,
              });
            } else {
              await waitForQueuedImage({
                outputKey: key,
                style: item.style,
                signal: controller.signal,
                onEstimate: handleEstimate,
                requireHd,
              });
            }
            break;
          } catch (error) {
            if (handleFailure(error)) return;
          }
        }
        if (controller.signal.aborted) return;
        waitControllers.delete(key);
        etaEstimates.delete(key);
        const isHd = requireHd || Boolean(lastData?.is_hd);
        const updated = item.media_kind === 'video'
          ? { ...item, is_hd: isHd, status: isHd ? 1 : 4, updated_at: new Date().toISOString() }
          : {
            ...item,
            is_hd: isHd,
            hires_pending: false,
            status: 1,
            updated_at: new Date().toISOString(),
          };
        itemByKey.set(key, updated);
        listCache.upsertItem(updated);
        if (requireHd) {
          if (item.media_kind === 'video') revokeStandaloneVideoObjectUrl(key);
          else revokeStandaloneImageObjectUrl(key);
        }
        const card = cardForKey(key);
        if (card) fillCardMedia(card, updated);
        if (openKey === key) void fillDialog(updated, { bust: requireHd });
        if (requireHd || isHd) applyHdFilterToCard(updated);
      } catch (error) {
        if (handleFailure(error)) return;
        markCardFailed(key);
        waitControllers.delete(key);
        etaEstimates.delete(key);
      }
    })();
  };

  const revealVideoPreview = async (card, item) => {
    let objectUrl = null;
    try {
      const token = await getAuthToken();
      objectUrl = await fetchStandaloneVideoObjectUrl({ token, outputKey: item.output_key });
    } catch {
      return;
    }
    if (!objectUrl || !card.isConnected) return;
    const current = itemByKey.get(item.output_key) || item;
    if (isStandaloneMediaReady(current)) return;
    fillCardMedia(card, current, { pending: true });
    watchJob(current, { requireHd: true });
  };

  const closeDialog = () => {
    modal.classList.remove('show');
    resumeGridVideos();
    openKey = null;
    dialogPending?.classList.add('hidden');
    dialogMedia?.classList.remove('is-pending-empty');
    if (dialogVideo) dialogVideo.removeAttribute('src');
    if (dialogImg) dialogImg.removeAttribute('src');
    if (dialogDownload) {
      dialogDownload.disabled = true;
      dialogDownload.setAttribute('aria-disabled', 'true');
    }
  };

  const setDownloadReady = (isReady, isVideo) => {
    if (!dialogDownload) return;
    dialogDownload.disabled = !isReady;
    dialogDownload.setAttribute('aria-disabled', isReady ? 'false' : 'true');
    const label = isVideo
      ? getT('download_video', 'Download video')
      : getT('download_image', 'Download image');
    dialogDownload.setAttribute('aria-label', label);
  };

  const videoBlobSrc = () => {
    const src = dialogVideo?.currentSrc || dialogVideo?.src;
    return typeof src === 'string' && src.startsWith('blob:') ? src : '';
  };

  const downloadFilename = (outputKey, isVideo) => {
    const base = String(outputKey || 'media').toLowerCase().replace(/[^a-z0-9._-]+/g, '_') || 'media';
    return `${base}.${isVideo ? 'mp4' : 'jpg'}`;
  };

  const triggerDownload = (href, filename) => {
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const encodeImageJpeg = (imageEl) => new Promise((resolve, reject) => {
    const width = imageEl.naturalWidth;
    const height = imageEl.naturalHeight;
    if (!width || !height) {
      reject(new Error('Image is not ready'));
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Canvas is not available'));
      return;
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(imageEl, 0, 0, width, height);
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to encode JPEG'));
        return;
      }
      resolve(blob);
    }, 'image/jpeg', 0.92);
  });

  const handleDownloadClick = async () => {
    const item = itemByKey.get(openKey);
    if (!item || !dialogDownload || dialogDownload.disabled) return;
    const isVideo = item.media_kind === 'video';
    const filename = downloadFilename(item.output_key, isVideo);
    if (isVideo) {
      const src = videoBlobSrc();
      if (!src) return;
      try {
        triggerDownload(src, filename);
      } catch (error) {
        console.error('MyGallery download failed', error);
        showError(getT('download_failed', 'Could not download'));
      }
      return;
    }
    const imageEl = dialogImg;
    if (!imageEl) return;
    const sourceKey = item.output_key;
    dialogDownload.disabled = true;
    dialogDownload.setAttribute('aria-disabled', 'true');
    try {
      await imageEl.decode();
      if (openKey !== sourceKey) return;
      const blob = await encodeImageJpeg(imageEl);
      if (openKey !== sourceKey) return;
      const objectUrl = URL.createObjectURL(blob);
      triggerDownload(objectUrl, filename);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      console.error('MyGallery download failed', error);
      showError(getT('download_failed', 'Could not download'));
    } finally {
      if (openKey === sourceKey) setDownloadReady(true, false);
    }
  };

  const fillDialog = async (item, { bust = false } = {}) => {
    if (!item) return;
    const isVideo = item.media_kind === 'video';
    dialogPrompt.textContent = standalonePromptText(item.prompt) || getT('empty_prompt', 'No prompt');
    const loras = standaloneLoraList(item.lora_activation);
    dialogLoras.replaceChildren();
    if (loras.length === 0) {
      const empty = document.createElement('li');
      empty.setAttribute('data-translate', 'no_loras');
      empty.textContent = getT('no_loras', 'No LoRAs');
      dialogLoras.appendChild(empty);
    } else {
      loras.forEach((lora) => {
        const row = document.createElement('li');
        row.textContent = `${lora.id} @ ${lora.strength}`;
        dialogLoras.appendChild(row);
      });
    }
    const isRealism = item.style === 'realism';
    const width = Number(item.width);
    const height = Number(item.height);
    const size = width > 0 && height > 0
      ? clampStandaloneImageSize(width, height)
      : null;
    const hasSize = Boolean(size);
    dialogSizeGroup?.classList.toggle('hidden', !hasSize);
    if (dialogSize) dialogSize.textContent = hasSize ? `${size.width} \u00d7 ${size.height}` : '';
    const seed = parseGenerationSeed(item.generation_seed);
    const hasSeed = seed != null;
    dialogSeedGroup?.classList.toggle('hidden', !hasSeed);
    dialogFacts?.classList.toggle('hidden', !hasSize && !hasSeed);
    if (dialogSeed) dialogSeed.textContent = hasSeed ? String(seed) : '';
    dialogSimilar.classList.remove('hidden');
    dialogSimilar.setAttribute('data-translate', isVideo ? 'create_similar_video' : 'create_similar');
    dialogSimilar.textContent = isVideo
      ? getT('create_similar_video', 'Create similar video')
      : getT('create_similar', 'Create similar image');
    dialogDelete.textContent = isVideo ? getT('delete_video', 'Delete video') : getT('delete_image', 'Delete image');
    const isHd = Boolean(item.is_hd);
    const jobPending = !isStandaloneMediaReady(item);
    const pending = Boolean(item.hires_pending) || item.status === 3;
    const showImageHd = !isVideo && !isHd && !pending && !jobPending;
    const showVideoHd = isVideo && item.status === 4 && !isHd && !jobPending;
    dialogUpgrade.classList.toggle('hidden', !(showImageHd || showVideoHd));
    dialogUpgrade.textContent = getT('upgrade_hd', 'Upgrade to HD');
    dialogDone.classList.toggle('hidden', !isHd || jobPending);
    dialogToVideo?.classList.toggle('hidden', isVideo);
    if (dialogToVideo) {
      dialogToVideo.disabled = !isHd || isVideo;
      dialogToVideo.setAttribute('aria-disabled', dialogToVideo.disabled ? 'true' : 'false');
    }
    dialogVideoHint?.classList.toggle('hidden', isVideo || isHd);
    const setUpgradeDisabled = (isDisabled) => {
      dialogUpgrade.disabled = isDisabled;
      dialogUpgrade.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
    };
    if (item.hires_pending) {
      dialogCredits.classList.remove('hidden', 'insufficient');
      dialogCredits.textContent = getT('upgrading', 'Upgrading to HD…');
      setUpgradeDisabled(true);
    } else if (jobPending) {
      dialogCredits.classList.add('hidden');
      dialogCredits.classList.remove('insufficient');
      setUpgradeDisabled(true);
    } else if (showImageHd) {
      const { cost, label } = hdUpgradeCostLabel({
        style: item.style,
        loraCount: loras.length,
        width: size?.width,
        height: size?.height,
        template: getT('upgrade_credits', 'HD upgrade = {cost} credits (You have {available} credits)'),
      });
      const isInsufficient = cost > getUserCredits();
      dialogCredits.classList.remove('hidden');
      dialogCredits.classList.toggle('insufficient', isInsufficient);
      dialogCredits.textContent = label;
      setUpgradeDisabled(isInsufficient);
    } else if (showVideoHd) {
      const cost = computeVideoUnitCost({
        operation: 'video_hd',
        loraCount: countVideoLoraEntries(item.lora_activation),
      });
      const isInsufficient = cost > getUserCredits();
      dialogCredits.classList.remove('hidden');
      dialogCredits.classList.toggle('insufficient', isInsufficient);
      dialogCredits.textContent = interpolateCreditTemplate(
        getT('upgrade_credits', 'HD upgrade = {cost} credits (You have {available} credits)'),
        { cost, available: getUserCredits(), total: cost, count: 1 },
      );
      setUpgradeDisabled(isInsufficient);
    } else {
      dialogCredits.classList.add('hidden');
      dialogCredits.classList.remove('insufficient');
      setUpgradeDisabled(false);
    }
    const lang = getCurrentLanguage();
    if (lang) applyTranslations('my-gallery', lang);
    dialogSimilar.setAttribute('aria-label', dialogSimilar.textContent);
    const styleLabel = isRealism
      ? getT('style_realism', 'Realism')
      : getT('style_anime', 'Anime');
    const mediaLabel = getT(isVideo ? 'dialog_title_video' : 'dialog_title', isVideo ? 'Video' : 'Image');
    if (dialogTitle) {
      dialogTitle.textContent = getT('dialog_title_with_style', '{style} {media}')
        .replaceAll('{style}', styleLabel)
        .replaceAll('{media}', mediaLabel);
    }
    setDownloadReady(false, isVideo);
    dialogPending?.classList.toggle('hidden', !jobPending);
    if (jobPending && dialogEta) dialogEta.textContent = etaLabel(item.output_key);
    const token = await getAuthToken();
    if (openKey !== item.output_key) return;
    if (jobPending) {
      const imagePreview = !isVideo && Boolean(item.hires_pending);
      dialogMedia?.classList.toggle('is-pending-empty', !imagePreview);
      if (!isVideo && !imagePreview) {
        dialogImg?.classList.add('hidden');
        dialogVideo?.classList.add('hidden');
        dialogImg?.removeAttribute('src');
        dialogVideo?.removeAttribute('src');
        return;
      }
      if (isVideo) {
        dialogImg?.classList.add('hidden');
        try {
          const objectUrl = await fetchStandaloneVideoObjectUrl({ token, outputKey: item.output_key });
          if (openKey !== item.output_key) return;
          if (!objectUrl || !dialogVideo) throw new Error('missing video');
          dialogVideo.classList.remove('hidden');
          dialogVideo.src = objectUrl;
          dialogMedia?.classList.remove('is-pending-empty');
          dialogCredits.classList.remove('hidden', 'insufficient');
          dialogCredits.textContent = getT('upgrading', 'Upgrading to HD…');
          setUpgradeDisabled(true);
        } catch {
          if (openKey !== item.output_key) return;
          dialogVideo?.classList.add('hidden');
          dialogVideo?.removeAttribute('src');
          dialogMedia?.classList.add('is-pending-empty');
        }
        return;
      }
      dialogVideo?.classList.add('hidden');
      dialogImg?.classList.remove('hidden');
      const objectUrl = await fetchStandaloneImageObjectUrl({ token, outputKey: item.output_key });
      if (objectUrl && openKey === item.output_key && dialogImg) dialogImg.src = objectUrl;
      return;
    }
    dialogMedia?.classList.remove('is-pending-empty');
    if (isVideo) {
      dialogImg?.classList.add('hidden');
      dialogVideo?.classList.remove('hidden');
      const objectUrl = await fetchStandaloneVideoObjectUrl({ token, outputKey: item.output_key, bust });
      if (objectUrl && openKey === item.output_key && dialogVideo) {
        dialogVideo.src = objectUrl;
        setDownloadReady(true, true);
      }
    } else {
      dialogVideo?.classList.add('hidden');
      dialogImg?.classList.remove('hidden');
      const objectUrl = await fetchStandaloneImageObjectUrl({ token, outputKey: item.output_key, bust });
      if (objectUrl && openKey === item.output_key) {
        dialogImg.src = objectUrl;
        setDownloadReady(true, false);
      }
    }
  };

  const openDialog = (item) => {
    openKey = item.output_key;
    void fillDialog(item);
    modal.classList.add('show');
    pauseGridVideos();
  };

  const renderCards = (items, { append }) => {
    if (!append) {
      abortAllWaits();
      gridEl.replaceChildren();
    }
    items.forEach((item) => {
      itemByKey.set(item.output_key, item);
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'my-gallery-card';
      card.tabIndex = 0;
      card.setAttribute('data-output-key', item.output_key);
      if (isStandaloneMediaReady(item)) {
        fillCardMedia(card, item);
      } else {
        const imagePreview = item.media_kind !== 'video' && Boolean(item.hires_pending);
        if (imagePreview) fillCardMedia(card, item, { pending: true });
        else paintTimerOnlyCard(card, item);
        watchJob(item, { requireHd: imagePreview });
        if (item.media_kind === 'video' && item.status === 3) void revealVideoPreview(card, item);
      }
      card.addEventListener('click', () => {
        const current = itemByKey.get(item.output_key) || item;
        openDialog(current);
      });
      gridEl.appendChild(card);
    });
  };

  let nextCursor = null;
  let hasMore = false;
  let isLoading = false;
  let isLoadingMore = false;
  let sessionLoaded = false;
  let loadGeneration = 0;

  const setHasMore = (value) => {
    hasMore = Boolean(value);
    if (triggerEl) triggerEl.hidden = !hasMore;
  };

  const fetchPage = async (cursor) => {
    const token = await getAuthToken();
    if (!token) {
      const error = new Error('login');
      error.code = 'login';
      throw error;
    }
    return fetchStandaloneMediaPage({
      token,
      cursor,
      filter: mediaFilter,
      hd: hdFilter,
      prompt: promptFilter,
      limit: PAGE_LIMIT,
    });
  };

  const applyListResult = (result, { append }) => {
    const items = Array.isArray(result?.items) ? result.items : [];
    matchedCount = Number.isFinite(Number(result?.totalCount)) ? Number(result.totalCount) : items.length;
    paintCount(true);
    if (!append && items.length === 0) {
      itemByKey.clear();
      renderStatus(gridEl, emptyKind(), mediaFilter);
      nextCursor = result?.nextCursor || null;
      setHasMore(Boolean(nextCursor));
      sessionLoaded = true;
      return;
    }
    if (!append) itemByKey.clear();
    renderCards(items, { append });
    nextCursor = result?.nextCursor || null;
    setHasMore(Boolean(nextCursor));
    sessionLoaded = true;
  };

  const load = async () => {
    if (sessionLoaded || isLoading) {
      setHasMore(Boolean(nextCursor));
      return;
    }
    isLoading = true;
    const generation = ++loadGeneration;

    try {
      if (!(await isUserLoggedIn())) {
        if (generation !== loadGeneration) return;
        listCache.clear();
        abortAllWaits();
        renderStatus(gridEl, 'login', mediaFilter);
        paintCount(false);
        sessionLoaded = true;
        return;
      }
      const cached = listCache.get(mediaFilter, hdFilter, promptFilter);
      if (cached) {
        if (generation !== loadGeneration) return;
        abortAllWaits();
        applyListResult(cached, { append: false });
        return;
      }
      nextCursor = null;
      setHasMore(false);
      abortAllWaits();
      renderStatus(gridEl, 'loading', mediaFilter);
      paintCount(false);
      const result = await fetchPage(null);
      if (generation !== loadGeneration) return;
      listCache.put(mediaFilter, hdFilter, promptFilter, result);
      applyListResult(result, { append: false });
    } catch (error) {
      if (generation !== loadGeneration) return;
      renderStatus(gridEl, error.code === 'login' ? 'login' : 'error', mediaFilter);
      paintCount(false);
      sessionLoaded = false;
    } finally {
      if (generation === loadGeneration) isLoading = false;
    }
  };

  const loadMore = async () => {
    if (!hasMore || !nextCursor || isLoadingMore) return;
    if (!(await isUserLoggedIn())) return;
    const generation = loadGeneration;
    isLoadingMore = true;
    try {
      const result = await fetchPage(nextCursor);
      if (generation !== loadGeneration) return;
      listCache.append(mediaFilter, hdFilter, promptFilter, result);
      const items = Array.isArray(result?.items) ? result.items : [];
      if (Number.isFinite(Number(result?.totalCount))) matchedCount = Number(result.totalCount);
      paintCount(true);
      if (items.length > 0) renderCards(items, { append: true });
      nextCursor = result?.nextCursor || null;
      setHasMore(Boolean(nextCursor));
    } catch (error) {
      console.error('MyGallery.loadMore', error);
    } finally {
      if (generation === loadGeneration) isLoadingMore = false;
    }
  };

  const reloadFromFilters = () => {
    syncGalleryQuery();
    abortAllWaits();
    sessionLoaded = false;
    isLoading = false;
    void load();
  };

  const setMediaFilter = (nextFilter) => {
    mediaFilter = nextFilter;
    syncTabState(filterTabs, 'filter', nextFilter);
    reloadFromFilters();
  };

  const setHdFilter = (nextHd) => {
    hdFilter = nextHd;
    syncTabState(hdTabs, 'hd', nextHd);
    reloadFromFilters();
  };

  const applyPromptFilter = (nextPrompt) => {
    const trimmed = String(nextPrompt || '').trim().slice(0, PROMPT_FILTER_MAX);
    if (trimmed === promptFilter) return;
    promptFilter = trimmed;
    loadGeneration += 1;
    reloadFromFilters();
  };

  const applyHdFilterToCard = (updated) => {
    const matches = hdFilter === 'both'
      || (hdFilter === 'hires' && Boolean(updated.is_hd))
      || (hdFilter === 'lowres' && !updated.is_hd);
    if (matches) return;
    const key = updated.output_key;
    itemByKey.delete(key);
    gridEl.querySelector(`[data-output-key="${CSS.escape(String(key))}"]`)?.remove();
    matchedCount = Math.max(0, matchedCount - 1);
    paintCount(true);
    if (openKey === key) closeDialog();
    if (!gridEl.querySelector('.my-gallery-card')) {
      if (hasMore && nextCursor) void loadMore();
      else renderStatus(gridEl, emptyKind(), mediaFilter);
    }
  };

  dialogUpgrade.addEventListener('click', async () => {
    const item = itemByKey.get(openKey);
    if (!item) return;
    const token = await getAuthToken();
    if (!token) return;
    const isVideo = item.media_kind === 'video';
    if (!isVideo && (item.is_hd || item.hires_pending)) return;
    if (isVideo && item.status !== 4) return;
    const key = item.output_key;
    if (upgradePostingKeys.has(key) || waitControllers.has(key)) return;
    const loraCount = standaloneLoraList(item.lora_activation).length;
    const cost = isVideo
      ? computeVideoUnitCost({
        operation: 'video_hd',
        loraCount: countVideoLoraEntries(item.lora_activation),
      })
      : hdUpgradeCostLabel({
        style: item.style,
        loraCount,
        width: item.width,
        height: item.height,
      }).cost;
    if (cost > getUserCredits()) return;
    upgradePostingKeys.add(key);
    try {
      if (isVideo) await requestStandaloneVideoHdUpgrade({ token, outputKey: key });
      else await requestStandaloneHdUpgrade({ token, outputKey: key });
    } catch (error) {
      console.error('MyGallery upgrade failed', error);
      showError(error.body?.error || error.message || getT('upgrade_failed', 'Could not request HD upgrade'));
      return;
    } finally {
      upgradePostingKeys.delete(key);
    }
    await syncSpriteCredits();
    showSuccess(getT('upgrade_queued', 'HD upgrade requested'));
    const pendingItem = isVideo
      ? { ...item, status: 3 }
      : { ...item, hires_pending: true };
    itemByKey.set(key, pendingItem);
    listCache.upsertItem(pendingItem);
    const card = cardForKey(key);
    if (card) fillCardMedia(card, pendingItem, { pending: true });
    const openItem = openKey === key ? pendingItem : itemByKey.get(openKey);
    if (openItem) void fillDialog(openItem);
    watchJob(pendingItem, { requireHd: true });
  });

  dialogToVideo?.addEventListener('click', () => {
    const item = itemByKey.get(openKey);
    if (!item || item.media_kind === 'video' || !item.is_hd) return;
    writeSimilarStandaloneVideo({ output_key: item.output_key });
    window.location.href = `/create-video.html?mode=video-text&kind=free&source=gallery&output_key=${encodeURIComponent(item.output_key)}`;
  });

  dialogSimilar.addEventListener('click', () => {
    const item = itemByKey.get(openKey);
    if (!item) return;
    const seed = parseGenerationSeed(item.generation_seed);
    if (item.media_kind === 'video') {
      const sourceImageKey = item.source_image_output_key ? String(item.source_image_output_key) : '';
      writeSimilarStandaloneVideo({
        prompt: standalonePromptText(item.prompt),
        style: item.style,
        loras: standaloneLoraList(item.lora_activation),
        seed,
        ...(sourceImageKey ? { output_key: sourceImageKey } : {}),
      });
      const similarUrl = new URL('/create-video.html', window.location.origin);
      similarUrl.searchParams.set('mode', 'video-text');
      similarUrl.searchParams.set('kind', 'free');
      if (sourceImageKey) {
        similarUrl.searchParams.set('source', 'gallery');
        similarUrl.searchParams.set('output_key', sourceImageKey);
      }
      if (seed != null) similarUrl.searchParams.set('seed', String(seed));
      window.location.href = `${similarUrl.pathname}${similarUrl.search}`;
      return;
    }
    const width = Number(item.width);
    const height = Number(item.height);
    const size = width > 0 && height > 0
      ? clampStandaloneImageSize(width, height)
      : null;
    writeSimilarStandaloneImage({
      prompt: standalonePromptText(item.prompt),
      negative: standaloneNegativeText(item.prompt),
      style: item.style,
      loras: standaloneLoraList(item.lora_activation),
      job_id: item.job_id,
      width: size?.width,
      height: size?.height,
      seed,
    });
    const similarUrl = new URL('/create-video.html', window.location.origin);
    similarUrl.searchParams.set('kind', 'free');
    if (size) {
      similarUrl.searchParams.set('width', String(size.width));
      similarUrl.searchParams.set('height', String(size.height));
    }
    if (seed != null) similarUrl.searchParams.set('seed', String(seed));
    window.location.href = `${similarUrl.pathname}${similarUrl.search}`;
  });

  dialogDelete.addEventListener('click', async () => {
    const item = itemByKey.get(openKey);
    if (!item) return;
    const confirmed = await showConfirm(getT('delete_confirm', 'Delete this image? This cannot be undone.'), {
      type: 'error',
      yesText: getT('delete_yes', 'Delete'),
      noText: getT('delete_no', 'Cancel'),
      icon: 'trash',
    });
    if (!confirmed) return;
    const token = await getAuthToken();
    if (!token) return;
    dialogDelete.disabled = true;
    try {
      if (item.media_kind === 'video') {
        await requestStandaloneVideoDelete({ token, outputKey: item.output_key });
        revokeStandaloneVideoObjectUrl(item.output_key);
      } else {
        await requestStandaloneImageDelete({ token, outputKey: item.output_key });
        revokeStandaloneImageObjectUrl(item.output_key);
      }
      itemByKey.delete(item.output_key);
      listCache.removeItem(item.output_key);
      gridEl.querySelector(`[data-output-key="${item.output_key}"]`)?.remove();
      closeDialog();
      showSuccess(getT('delete_success', 'Image deleted'));
      matchedCount = Math.max(0, matchedCount - 1);
      paintCount(true);
      if (!gridEl.querySelector('.my-gallery-card')) {
        if (hasMore && nextCursor) {
          void loadMore();
        } else {
          renderStatus(gridEl, emptyKind(), mediaFilter);
        }
      }
    } catch (error) {
      console.error('MyGallery delete failed', error);
      showError(error.body?.error || error.message || getT('delete_failed', 'Could not delete image'));
    } finally {
      dialogDelete.disabled = false;
    }
  });

  syncTabState(filterTabs, 'filter', mediaFilter);
  filterTabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const nextFilter = tab.dataset.filter || 'both';
      loadGeneration += 1;
      setMediaFilter(nextFilter);
    });
  });

  syncTabState(hdTabs, 'hd', hdFilter);
  hdTabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const nextHd = tab.dataset.hd || 'both';
      loadGeneration += 1;
      setHdFilter(nextHd);
    });
  });

  if (promptInput) {
    promptInput.addEventListener('input', () => {
      if (promptTimer) clearTimeout(promptTimer);
      promptTimer = setTimeout(() => {
        promptTimer = null;
        applyPromptFilter(promptInput.value);
      }, PROMPT_DEBOUNCE_MS);
    });
    promptInput.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      if (promptTimer) {
        clearTimeout(promptTimer);
        promptTimer = null;
      }
      applyPromptFilter(promptInput.value);
    });
    promptInput.addEventListener('search', () => {
      if (promptTimer) {
        clearTimeout(promptTimer);
        promptTimer = null;
      }
      applyPromptFilter(promptInput.value);
    });
  }

  dialogDownload?.addEventListener('click', handleDownloadClick);

  closeBtn.addEventListener('click', closeDialog);
  modal.addEventListener('click', (event) => {
    if (event.target === modal) closeDialog();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modal.classList.contains('show')) closeDialog();
  });

  observeWhenVisible(triggerEl, () => {
    void loadMore();
  }, { rootMargin: '100px', threshold: 0.1 });

  const handleAuthChanged = () => {
    abortAllWaits();
    listCache.clear();
    sessionLoaded = false;
    isLoading = false;
    isLoadingMore = false;
    nextCursor = null;
    loadGeneration += 1;
    setHasMore(false);
    closeDialog();
    void syncSpriteCredits();
    void load();
  };

  document.addEventListener('auth:login:success', handleAuthChanged);
  document.addEventListener('auth:logout', handleAuthChanged);

  void Promise.all([loadCreditPricing(), syncSpriteCredits()]).then(() => load());
};
